import React, { useEffect, useMemo, useState } from "react";
import ExtendedTaskList from "./ExtendedTaskList";
import { DEFAULT_TABLE_COLUMNS } from "../../utils/tableColumns";
import { sortTasks } from "../../utils/taskSort";

/**
 * Flattens the nested projects → tracks → tasks structure the pages fetch into
 * the flat task rows the table expects, copying project/epic/track context onto
 * each task (the table renders these as columns).
 *
 * @param {Array} projects - Nested projects
 * @returns {Array} Flat task rows
 */
const flattenProjects = (projects = []) => {
  const rows = [];
  (projects || []).forEach((project) => {
    const projectCtx = {
      project: project.name,
      project_id: project.id,
      epic: project.epic,
      epic_id: project.epic_id,
    };
    if (Array.isArray(project.tracks) && project.tracks.length > 0) {
      project.tracks.forEach((track) => {
        (track.tasks || []).forEach((task) => {
          rows.push({
            ...task,
            ...projectCtx,
            track: track.name,
            track_id: track.id,
          });
        });
      });
    }
    // Some endpoints attach tasks directly to the project (no track level).
    (project.tasks || []).forEach((task) => {
      rows.push({ ...task, ...projectCtx });
    });
  });
  return rows;
};

/**
 * Reusable Table view for the TaskManagement pages. Takes the same nested
 * projects props as the List/Kanban views, flattens them for the shared
 * {@link ExtendedTaskList} table, and applies the page-level sort. Column
 * visibility is owned by the page (chooser lives in the shared toolbar).
 *
 * @param {Object} props
 * @param {Array} props.projects - Nested projects → tracks → tasks
 * @param {function} props.fetchProjects - Refetches the page's nested data
 * @param {function} props.setProjects - Updates the page's nested data
 * @param {Object} [props.visibleCols] - Column visibility map
 * @param {Array} [props.sortCriteria] - Active sort, applied across all rows
 * @returns {React.ReactElement}
 */
const TableView = ({
  projects = [],
  fetchProjects,
  setProjects,
  visibleCols = DEFAULT_TABLE_COLUMNS,
  sortCriteria = [],
}) => {
  const flattened = useMemo(() => flattenProjects(projects), [projects]);
  const [tasks, setTasks] = useState(() => sortTasks(flattened, sortCriteria));

  // Re-sync (and re-sort) whenever the page refetches or the sort changes.
  useEffect(() => {
    setTasks(sortTasks(flattened, sortCriteria));
  }, [flattened, sortCriteria]);

  /**
   * Refetches the page's nested data (so the other views stay current) and
   * returns flat rows for the table. ExtendedTaskList calls this after edits.
   */
  const fetchTasks = async () => {
    if (!fetchProjects) return tasks;
    const fresh = await fetchProjects();
    if (setProjects && Array.isArray(fresh)) setProjects(fresh);
    return sortTasks(flattenProjects(fresh), sortCriteria);
  };

  return (
    <ExtendedTaskList
      tasks={tasks}
      fetchTasks={fetchTasks}
      setTasks={setTasks}
      visibleCols={visibleCols}
    />
  );
};

export default TableView;
